/*
  lead-capture.js — <rns-lead-capture>

  One behavior, one file (§6). Vanilla, deferred, and the section it drives is
  display:none under .no-js, so nothing here is load-bearing for a purchase.

  ── The floors are here, not in the schema ──────────────────────────────────
  The brief says the popup must not cover the hero on load and must appear only
  after meaningful engagement. Those are enforced as constants below rather than
  as setting defaults, because a default is something a merchant can lower and a
  floor is not. No combination of settings produces a popup over the hero.

    MIN_SCROLL_PX   one full viewport. Until the visitor has scrolled past the
                    first screen, nothing can open — which is the structural
                    form of "never covers the hero".
    MIN_DWELL_MS    a floor under the dwell setting, so a mis-set value cannot
                    turn this into an on-arrival interruption.

  ── Every uncertainty resolves toward NOT showing it ────────────────────────
  A storage failure counts as already shown. An unreadable version counts as
  already shown. Another dialog being open defers rather than stacks. The cost
  of not showing a popup is one missed email; the cost of showing it wrongly is
  a visitor who leaves. Those are not the same size.

  ?rns-debug on any URL narrates what it did and which guard stopped it.
*/
(function () {
  'use strict';

  var DEBUG = false;
  try {
    DEBUG = window.location.search.indexOf('rns-debug') !== -1;
  } catch (e) {
    DEBUG = false;
  }

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[rns-lead]');
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  var STORAGE_PREFIX = 'rns:lead:';
  var MIN_DWELL_MS = 8000;

  if (customElements.get('rns-lead-capture')) return;

  // Written as a class expression rather than a `class` declaration so this file
  // can be re-executed by the theme editor without a lexical redeclaration,
  // which is a hard SyntaxError rather than a subtle bug (Sessions 013 and 018).
  var RnsLeadCapture = class extends HTMLElement {
    constructor() {
      super();
      this.opened = false;
      this.settled = false;
      this.trigger = null;
      this.onKeydown = this.handleKeydown.bind(this);
      this.onScroll = this.handleScroll.bind(this);
      this.onMouseOut = this.handleMouseOut.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('.rns-lead__panel');
      this.version = this.dataset.rnsVersion || '';
      this.mode = this.dataset.rnsTrigger || 'both';

      var pct = parseInt(this.dataset.rnsScrollPercent, 10);
      this.scrollPercent = isNaN(pct) ? 50 : pct;

      var delay = parseInt(this.dataset.rnsDelay, 10) * 1000;
      this.dwellMs = isNaN(delay) ? MIN_DWELL_MS : Math.max(delay, MIN_DWELL_MS);

      if (!this.panel) {
        log('no panel found, standing down');
        return;
      }

      // The theme editor re-renders sections constantly. A popup that reopened
      // on every keystroke would make the section impossible to edit.
      if (window.Shopify && window.Shopify.designMode) {
        log('design mode: showing the panel in place, no triggers armed');
        this.hidden = false;
        this.classList.add('rns-lead--design-mode');
        return;
      }

      // The post-submit reload. This is the one state that opens on load, and
      // it is a response to something the visitor just did.
      //
      // Detected from the status element rather than from an attribute on this
      // wrapper: `form` is only defined inside the {% form %} block, so a
      // condition out here would be false forever and the confirmation would
      // never appear. The element that shows the result IS the signal.
      if (this.querySelector('.rns-lead__status')) {
        log('form was posted: opening with the result');
        this.suppress();
        this.open();
        this.bindDismiss();
        return;
      }

      if (this.alreadySettled()) {
        log('already dismissed or signed up for version', this.version);
        return;
      }

      this.bindDismiss();
      this.arm();
    }

    disconnectedCallback() {
      this.teardownTriggers();
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('rns-scroll-locked');
    }

    /* ---------- Suppression ---------- */

    storageKey() {
      return STORAGE_PREFIX + this.version;
    }

    alreadySettled() {
      try {
        return window.localStorage.getItem(this.storageKey()) !== null;
      } catch (e) {
        // Storage unavailable — private mode, blocked cookies, quota. Counting
        // this as "already shown" means the visitor is never nagged on a device
        // where a dismissal could not be remembered.
        log('storage unreadable, treating as already shown');
        return true;
      }
    }

    suppress() {
      this.settled = true;
      try {
        window.localStorage.setItem(this.storageKey(), String(Date.now()));
      } catch (e) {
        log('storage unwritable; this visit will not be remembered');
      }
    }

    /* ---------- Arming ---------- */

    arm() {
      this.dwellPassed = false;
      this.dwellTimer = window.setTimeout(() => {
        this.dwellPassed = true;
        log('dwell reached at', this.dwellMs, 'ms');
        this.maybeOpen('dwell');
      }, this.dwellMs);

      if (this.mode === 'scroll' || this.mode === 'both') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      // Exit intent is a pointer leaving through the top of the viewport. A
      // touch device never produces it, which is why it is never the only
      // trigger the section recommends.
      if (this.mode === 'exit' || this.mode === 'both') {
        document.addEventListener('mouseout', this.onMouseOut);
      }

      log('armed:', this.mode, '· scroll', this.scrollPercent + '%', '· dwell', this.dwellMs + 'ms');
    }

    teardownTriggers() {
      window.clearTimeout(this.dwellTimer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onMouseOut);
    }

    /*
      The floor. One full viewport of scrolling, whatever the settings say —
      this is what makes "never covers the hero" structural rather than a
      default somebody could lower.
    */
    pastTheFold() {
      return window.pageYOffset >= window.innerHeight;
    }

    scrolledEnough() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return false;
      var reached = (window.pageYOffset / scrollable) * 100;
      return reached >= this.scrollPercent;
    }

    handleScroll() {
      if (this.opened || this.settled) return;
      if (!this.scrolledEnough()) return;
      this.maybeOpen('scroll');
    }

    handleMouseOut(event) {
      if (this.opened || this.settled) return;
      // relatedTarget is null only when the pointer has left the document
      // entirely; clientY at or above zero means it left through the top,
      // toward the browser's own controls rather than toward the taskbar.
      if (event.relatedTarget || event.clientY > 0) return;
      this.maybeOpen('exit');
    }

    maybeOpen(reason) {
      if (this.opened || this.settled) return;

      if (!this.dwellPassed) {
        log('deferred (' + reason + '): dwell not reached');
        return;
      }

      if (!this.pastTheFold()) {
        log('deferred (' + reason + '): still on the first screen');
        return;
      }

      if (reason === 'dwell' && !this.scrolledEnough() && this.mode !== 'exit') {
        log('deferred (dwell): scroll depth not reached');
        return;
      }

      // Another dialog owns the screen — the cart drawer, or its downsell.
      // Stacking a second one over it is the trap the brief bans.
      if (document.documentElement.classList.contains('rns-scroll-locked')) {
        log('deferred (' + reason + '): another dialog is open');
        return;
      }

      log('opening, triggered by', reason);
      this.open();
    }

    /* ---------- Open and close ---------- */

    open() {
      if (this.opened) return;
      this.opened = true;
      this.teardownTriggers();

      this.trigger = document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('rns-scroll-locked');
      document.addEventListener('keydown', this.onKeydown);

      window.requestAnimationFrame(() => {
        var status = this.querySelector('.rns-lead__status');
        if (status) {
          status.focus();
          return;
        }
        var field = this.querySelector('.rns-lead__input');
        if (field) {
          field.focus();
          return;
        }
        var close = this.querySelector('[data-rns-lead-close]');
        if (close) close.focus();
      });
    }

    close() {
      if (!this.opened) return;
      this.opened = false;
      this.hidden = true;
      document.documentElement.classList.remove('rns-scroll-locked');
      document.removeEventListener('keydown', this.onKeydown);
      this.suppress();

      if (this.trigger && document.contains(this.trigger) && this.trigger.focus) {
        this.trigger.focus();
      }
      log('closed and remembered');
    }

    bindDismiss() {
      var targets = this.querySelectorAll('[data-rns-lead-dismiss]');
      for (var i = 0; i < targets.length; i += 1) {
        targets[i].addEventListener('click', () => this.close());
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      var focusable = this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      var list = Array.prototype.filter.call(focusable, (el) => el.offsetParent !== null);
      if (!list.length) return;

      var first = list[0];
      var last = list[list.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  customElements.define('rns-lead-capture', RnsLeadCapture);
})();
